/**
 * Token Status Service
 * Manages token health monitoring and status information
 */

import { WithingsTokenService } from "./withings-token-service";

export interface TokenHealthStatus {
  /** Expiration timestamp */
  expiresAt: number;
  /** Formatted time string */
  formattedTimeLeft: string;
  /** Whether token is healthy */
  isHealthy: boolean;
  /** Status color class */
  statusColor: "critical" | "expired" | "healthy" | "unknown" | "warning";
  /** Time until expiration in milliseconds */
  timeUntilExpiry: number;
  /** User ID from token */
  userId: string;
}

/**
 * Token Status Service
 * Provides token health monitoring and status formatting
 */
export const TokenStatusService = {
  /**
   * Get current token health status
   *
   * @returns {Promise<null | TokenHealthStatus>} Token health status or null
   *
   */
  async getTokenHealth(): Promise<null | TokenHealthStatus> {
    try {
      const tokens = await WithingsTokenService.getStoredTokens();
      if (!tokens) {
        return null;
      }

      const now = Date.now();
      const timeUntilExpiry = Math.max(0, tokens.expiresAt - now);

      return {
        expiresAt: tokens.expiresAt,
        formattedTimeLeft: TokenStatusService.formatTimeLeft(timeUntilExpiry),
        isHealthy: timeUntilExpiry > 5 * 60 * 1000, // 5 minutes buffer
        statusColor: TokenStatusService.getStatusColor(timeUntilExpiry),
        timeUntilExpiry,
        userId: tokens.userId,
      };
    } catch {
      // NOTE: Failed to get token health
      return null;
    }
  },

  /**
   * Format time left in human readable format
   *
   * @param ms - Milliseconds until expiration
   *
   * @returns {string} Formatted time string
   *
   */
  formatTimeLeft(ms: number): string {
    if (ms === 0) {
      return "Abgelaufen";
    }

    const hours = Math.floor(ms / (1000 * 60 * 60));
    const minutes = Math.floor((ms % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((ms % (1000 * 60)) / 1000);

    if (hours > 0) {
      return `${String(hours)}h ${String(minutes)}m`;
    } else if (minutes > 0) {
      return `${String(minutes)}m ${String(seconds)}s`;
    } else {
      return `${String(seconds)}s`;
    }
  },

  /**
   * Get status color based on time remaining
   *
   * @param timeLeft - Milliseconds until expiration
   *
   * @returns {TokenHealthStatus["statusColor"]} Status color string
   *
   */
  getStatusColor(timeLeft: number): TokenHealthStatus["statusColor"] {
    if (timeLeft === 0) {
      return "expired";
    }
    if (timeLeft < 5 * 60 * 1000) {
      return "critical";
    } // < 5 minutes
    if (timeLeft < 15 * 60 * 1000) {
      return "warning";
    } // < 15 minutes
    return "healthy";
  },

  /**
   * Check if token needs proactive refresh
   *
   * @returns {Promise<boolean>} True if token should be refreshed
   *
   */
  async shouldRefreshToken(): Promise<boolean> {
    try {
      const health = await TokenStatusService.getTokenHealth();
      if (!health) {
        return false;
      }

      const refreshThreshold = 15 * 60 * 1000; // 15 minutes
      return health.timeUntilExpiry < refreshThreshold;
    } catch {
      // NOTE: Failed to check if token should refresh
      return false;
    }
  },
};
